import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {Avatar, Box, Typography} from '@mui/material';
import {GetStaticProps} from 'next';
import {useTranslation} from 'next-i18next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import Indicator from '../../components/campaign/indicator';
import TagElement from '../../components/campaign/tag';
import {Colors} from '../../components/common/colors';
import {Multiline} from '../../components/common/text';
import {CampaignList, CampaignOverview} from '../../types';
import {getCampaignStatus, getCampaignTimeLeft} from '../../utils/campaign';
import {getLocaleLabels} from '../../utils/lang';
import {URL_CLIENT, URL_SERVER_INTERNAL} from '../../utils/settings';

export default function Mini(props: CampaignStaticProps) {
    const {i18n, t} = useTranslation('common');

    const data = props.data;
    // console.log('CAMPAIGN = ', data);

    if (!data) {
        return <></>;
    }

    const campaign = data;

    const labels = getLocaleLabels(campaign.labels, i18n.language);
    const picture = labels.cover_url;
    const percentage = Math.round(
        campaign.goal > 0 ? (campaign.amount / campaign.goal) * 100 : 0
    );

    const authorPicture = campaign.author.picture_url;

    const status = getCampaignStatus(campaign);
    // console.log('STATUS = ', status);

    const timeLeft = getCampaignTimeLeft(campaign);
    // console.log('timeLeft = ', timeLeft);

    return (
        <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            sx={{backgroundColor: 'transparent'}}
        >
            <CampaignSeo data={data} />
            <Link
                href={'/' + campaign.url}
                style={{textDecoration: 'none', color: 'black'}}
                target="_blank"
            >
                <Box
                    sx={{
                        cursor: 'pointer',
                        width: 300,
                        boxShadow: 3,
                        borderRadius: '8px',
                        textDecoration: 'none',
                        backgroundColor: 'white',
                    }}
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                >
                    {/* <Typography>{t(status)}</Typography> */}
                    <Box display="flex" flexDirection="row" justifyContent="center">
                        {Boolean(picture) && (
                            <Image
                                src={picture}
                                alt={'Précommande'}
                                width={300}
                                height={176}
                                style={{
                                    borderTopLeftRadius: '8px',
                                    borderTopRightRadius: '8px',
                                    display: 'block',
                                }}
                            />
                        )}
                        {!Boolean(picture) && (
                            <HideImageOutlinedIcon
                                sx={{
                                    width: 200,
                                    height: 118,
                                    margin: 'auto',
                                }}
                            />
                        )}
                    </Box>
                    <Box
                        padding={'5px'}
                        sx={{
                            border: '1px solid #ccc',
                            borderBottomLeftRadius: '5px',
                            borderBottomRightRadius: '5px',
                            borderTop: '0px',
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            marginTop={'10px'}
                            alignItems="center"
                        >
                            <Box display="flex" flexDirection="row">
                                <Avatar
                                    alt={campaign.author.display_name}
                                    src={authorPicture}
                                    imgProps={{referrerPolicy: 'no-referrer'}}
                                    sx={{width: 25, height: 25}}
                                />
                                <Typography
                                    alignSelf={'center'}
                                    marginLeft={'5px'}
                                    sx={{
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        maxWidth: '250px',
                                    }}
                                >
                                    {campaign.author.display_name}
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="row"
                                alignSelf="center"
                                marginTop={1}
                            >
                                <LocalOfferIcon sx={{marginRight: '5px', fontSize: '25px'}} />
                                {campaign.tags.map((t) => (
                                    <TagElement
                                        tag={t.tag}
                                        key={t.tag.tag_id}
                                        sx={{maxWidth: '250px'}}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                marginTop: '10px',
                                marginBottom: '10px',
                                textDecoration: 'none',
                            }}
                        >
                            {labels.title}
                        </Typography>
                        <Multiline
                            numberOfLine={2}
                            typo={{
                                sx: {
                                    textAlign: 'justify',
                                    marginTop: '10px',
                                    textDecoration: 'none',
                                },
                            }}
                        >
                            <>{labels.subtitle}</>
                        </Multiline>
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-evenly"
                            marginTop={'10px'}
                            marginBottom={'10px'}
                        >
                            <Indicator
                                text={campaign.contribution.toString()}
                                icon={
                                    <GroupsIcon
                                        sx={{alignSelf: 'center', color: Colors.first}}
                                    />
                                }
                            />
                            <Indicator
                                text={timeLeft.amount.toString() + ' ' + t(timeLeft.short)}
                                icon={
                                    <CalendarMonthIcon
                                        sx={{alignSelf: 'center', color: Colors.first}}
                                    />
                                }
                            />
                        </Box>
                    </Box>
                </Box>
            </Link>
        </Box>
    );
}

type CampaignSeoProps = {
    data: CampaignOverview;
};

function CampaignSeo(props: CampaignSeoProps) {
    const labels = getLocaleLabels(props.data?.labels, 'fr');

    const urlFr = URL_CLIENT + '/mini/' + props.data?.url;
    const urlEn = URL_CLIENT + '/en/mini/' + props.data?.url;
    const title = labels.title;
    const subtitle = labels.subtitle;
    const image = labels.cover_url;

    return (
        <Head>
            <title>{title}</title>
            <meta name="description" content={subtitle} />
            <meta property="og:url" content={urlFr} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={subtitle} />
            <meta property="og:image" content={image} />
            <meta property="og:image:alt" content={title} />
            <meta property="og:image:width" content="640" />
            <meta property="og:image:height" content="360" />
            <meta property="og:ttl" content="2419200" />
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:description" content={subtitle} />
            <meta property="twitter:image" content={image} />
            <meta property="twitter:image:alt" content={title} />
            <meta property="twitter:url" content={urlFr} />
            <meta property="twitter:site" content="@KuneKune" />
            <link hrefLang="fr" href={urlFr} rel="canonical" />
            <link hrefLang="en" href={urlEn} rel="alternate" />
        </Head>
    );
}

type CampaignStaticProps = {
    data: CampaignOverview;
};

export const getStaticProps: GetStaticProps<CampaignStaticProps> = async (context) => {
    const translation = await serverSideTranslations(context.locale, ['common']);
    try {
        const campaign = context.params.url as string;
        let url = URL_SERVER_INTERNAL + '/campaign/overview?url=' + campaign;
        let requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const result = await fetch(url, requestOptions);
        const json = await result.json();

        return {
            props: {
                data: json,
                ...translation,
            },
            revalidate: 60,
        };
    } catch (e) {
        console.log('getServerSideProps::error compaign overview', e);
        return {
            props: {
                data: null,
                ...translation,
            },
            revalidate: 60,
        };
    }
};

export async function getStaticPaths({locales}) {
    try {
        let url = URL_SERVER_INTERNAL + '/campaign/list';
        let requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const result = await fetch(url, requestOptions);
        const urls = await result.json();

        const paths = urls
            ?.map((author: CampaignList) =>
                locales.map((locale) => ({
                    params: {url: author.url},
                    locale,
                }))
            )
            ?.flat();

        return {
            paths: paths,
            fallback: true,
        };
    } catch (e) {
        console.log('getStaticPaths::error ', e);

        return {
            paths: [],
            fallback: true,
        };
    }
}

Mini.getLayout = function getLayout(page: React.ReactNode) {
    return <>{page}</>;
};
